import { CSSResultGroup, html, LitElement, nothing, type PropertyValues } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { ProductMetaData } from 'components/discovery/pdp/meta-data/typing/meta-data.types.js'

import { BaseUrlController } from 'components/shared/enterprise-domains/base-url-controller.js'
import { style } from './meta-data.style.js'
import { createEffect } from '@cloudsuite/mosaic-shared/scripts/signals.js'
import { inject } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { PDP_PRODUCT_STORE, type PdpProductStore } from 'components/discovery/pdp/pdp-product/pdp-product-store.js'
import { HiddenFieldsMixin } from 'components/shared/hidden-fields/hidden-fields-mixin.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'

export const productMetaDataHiddenFields = ['variant-name', 'product-name', 'code', 'brand-name', 'rating'] as const

export const fontVariants = ['body', 'body-s', 'heading-1', 'heading-2', 'heading-3', 'heading-4', 'heading-5', 'heading-6', 'caption-l', 'caption-m', 'caption-s'] as const
export type fontVariantsType = typeof fontVariants

@customElement('csc-product-meta-data')
export class CSCProductMetaData extends HiddenFieldsMixin(LitElement, productMetaDataHiddenFields) {
  static override styles: CSSResultGroup = [hostStyle, style]

  private urls = new BaseUrlController(this)

  @inject(PDP_PRODUCT_STORE)
  private store!: PdpProductStore

  @property({ type: Number, attribute: 'product-id' })
  productId: number | null = null

  @property({ type: String, attribute: 'title-variant' })
  titleVariant: fontVariantsType[number] = 'heading-1'

  @property({ type: String, attribute: 'variant-title-variant' })
  variantTitleVariant: fontVariantsType[number] = 'heading-4'

  @state()
  private product: ProductMetaData | null = null

  @state()
  private hasSuffix = false

  private productLoaded: Promise<void> = Promise.resolve()

  constructor() {
    super()
    createEffect(() => {
      const product = this.store.product()
      if (product) this.product = product as ProductMetaData
    })
  }

  protected override willUpdate(changedProperties: PropertyValues): void {
    super.willUpdate(changedProperties)
    if (changedProperties.has('productId')) {
      this.productLoaded = this.store.loadProduct({ productId: this.productId })
    }
  }

  override async getUpdateComplete(): Promise<boolean> {
    await super.getUpdateComplete()
    await this.productLoaded
    return super.getUpdateComplete()
  }

  private handleSuffixSlotChange(event: Event) {
    this.hasSuffix = (event.target as HTMLSlotElement).assignedNodes({ flatten: true }).length > 0
  }

  override render() {
    if (!this.product) return nothing
    const brand = this.product.brand
    const variantName = this.isHidden('variant-name') ? null : this.product.variant?.name
    const showProductName = !!variantName && !this.isHidden('product-name')
    const showCode = !this.isHidden('code')
    const showBrandName = !this.isHidden('brand-name') && !!brand?.name
    const showRating = !this.isHidden('rating')
    const showSuffixDivider = this.hasSuffix && (showCode || showBrandName || showRating)
    return html`
      <div class="heading">
        <cse-text as="h1" variant=${this.titleVariant}>
            ${variantName || this.product.name}
        </cse-text>
        ${showProductName ? html`
          <cse-text class="product-name" as="h2" variant=${this.variantTitleVariant}>
              ${this.product.name}
          </cse-text>`
          : nothing
        }
        <div class="info">
          <cse-group direction="row" column-gap="06" alignment="center">
            <cse-group class="details" direction="row" column-gap="06">
              ${showCode ? html`
                <cse-text as="p" variant="body-s">
                  <span class="label">${t('product_number', { ns: 'csc-product-meta-data', default: 'Product nr.' })}</span>
                  ${this.product.code}
                </cse-text>`
                : nothing
              }

              ${showBrandName && showCode ? html`<cse-divider orientation="vertical" variant="solid"></cse-divider>` : nothing}

              ${showBrandName ? html`
                <cse-text as="p" variant="body-s">
                  <span class="label">${t('brand', { ns: 'csc-product-meta-data', default: 'Brand:' })}</span>
                  ${brand?.slug ? html`
                    <cse-link
                      url=${this.urls.path(`${brand.slug}/`)}
                      font-variant="body-s"
                      variant="primary"
                    >
                      ${brand.name}
                    </cse-link>`
                    : brand?.name
                  }
                </cse-text>
                `
                : nothing
              }

              ${showRating && (showCode || showBrandName) ? html`<cse-divider orientation="vertical" variant="solid"></cse-divider>` : nothing}

              ${showRating ? html`
                <csc-product-rating
                  value=${this.product.rating?.main?.value ?? 0}
                  count=${this.product.rating?.main?.count ?? 0}>
                </csc-product-rating>`
                : nothing
              }

              ${showSuffixDivider ? html`<cse-divider class="suffix-divider" orientation="vertical" variant="solid"></cse-divider>` : nothing}
            </cse-group>

            <slot name="suffix" @slotchange=${this.handleSuffixSlotChange}></slot>
          </cse-group>
        </div>
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-product-meta-data': CSCProductMetaData
  }
}
